import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import isEqual from 'lodash/isEqual';
import { Subject } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { DropdownService } from 'src/app/project/components/dropdown/dropdown-service/dropdown.service';
import { ResponseErrorService } from 'src/app/project/modules/errors/response-error.service';
import { PreferencesService } from 'src/app/project/modules/preferences/preferences-service/preferences.service';
import { TSavedView } from 'src/app/project/modules/saved-views/models/saved-view.model';
import { SavedViewsService } from 'src/app/project/modules/saved-views/saved-views.service';
import { PermissionsService } from 'src/app/project/modules/share/permissions.service';
import { TUser } from 'src/app/project/modules/user/user.model';
import { UserService } from 'src/app/project/modules/user/user.service';
import { checkCustomWorkspaceId } from 'src/app/project/modules/workspace/workspace';
import { TWorkspace, TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';
import { WorkspaceService } from 'src/app/project/modules/workspace/workspace.service';
import { ActiveService } from 'src/app/project/services/active/active.service';
import { logEventInGTAG } from 'src/app/project/services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventGrouping,
  EGoogleEventSite,
} from 'src/app/project/services/analytics/google-analytics.consts';
import { GoogleAnalyticsService } from 'src/app/project/services/analytics/google-analytics.service';
import { iconMap } from 'src/app/project/shared/enums/icon-map';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { ResetTableService } from '../../../reset-table.service';
import { TColumn } from '../../../site-table/columns/column.model';
import { groupingSignal } from '../../../site-table/columns/grouping.store';
import { TableColumnsService } from '../../../site-table/columns/table-columns.service';
import { CustomTableService } from '../../../site-table/custom-table/custom-table.service';
import {
  SiteTableColumnsDataService,
  TColumnsData,
} from '../../../site-table/site-table-columns-data.service';
import { ESortOrder } from '../../../site-table/sort-order-enum';
import { GET_COLLAPSED_GROUPS } from '../../../site-table/table.ui.store';
import { GroupButtonDropdownService } from './group-button-dropdown.service';

type TGroupingSnapshot = Pick<TColumn, 'name' | 'groupIndex' | 'groupOrder'>;

@Component({
  selector: 'pp-group-button-dropdown',
  templateUrl: './group-button-dropdown.component.html',
  styleUrls: ['./group-button-dropdown.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupButtonDropdownComponent implements OnInit, OnDestroy {
  columns: TColumnsData = this.siteTableColumnsDataService.getColumns();
  private readonly destroy$ = new Subject<void>();

  public tagsVisible: boolean;
  public user: TUser;
  workspace: TWorkspace;
  workspaces: TWorkspacesById;
  siteId = '';
  EIconPath = EIconPath;
  iconMap = iconMap;

  readonly grouping = groupingSignal;
  readonly emptyGroupsVisible = signal<number[]>([]);
  readonly saveViewProcessing = signal(false);
  readonly scrollPosition = signal(0);
  readonly scrolledToBottom = signal(false);

  isDropdownVisible: boolean;
  activeViewName = '';
  activeView: TSavedView;

  private cachedGrouping: TGroupingSnapshot[] = [];
  private scrollRaf: number | null = null;

  constructor(
    private dropdownService: DropdownService,
    private siteTableColumnsDataService: SiteTableColumnsDataService,
    private workspaceService: WorkspaceService,
    private activeService: ActiveService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private userService: UserService,
    private permissionsService: PermissionsService,
    private preferencesService: PreferencesService,
    private groupButtonDropdownService: GroupButtonDropdownService,
    private customTableService: CustomTableService,
    private savedViewsService: SavedViewsService,
    private responseErrorService: ResponseErrorService,
    private tableColumnsService: TableColumnsService,
    private resetTableService: ResetTableService,
  ) {}

  ngOnInit() {
    this.cachedGrouping = this.grouping().map((col) => ({
      name: col.name,
      groupIndex: col.groupIndex,
      groupOrder: col.groupOrder,
    }));

    this.workspace = this.workspaceService.getActiveWorkspace();
    this.workspaces = this.workspaceService.getWorkspaces();
    this.user = this.userService.getUser();

    if (this.workspace) {
      const tagPermissions = this.permissionsService.getTagPermissions(this.workspace?.workspaceId);
      this.siteId = checkCustomWorkspaceId() || this.workspace?.workspaceId;
      this.tagsVisible = tagPermissions.read;
    } else {
      this.columns = this.siteTableColumnsDataService.getAllColumns();
      this.tagsVisible = true;
      this.siteId = checkCustomWorkspaceId();
    }

    const activeViewId = this.savedViewsService.getSelectedViewId(this.siteId);
    this.activeView = this.savedViewsService.getView(activeViewId);
    this.activeViewName = this.activeView?.name;
  }

  ngOnDestroy(): void {
    const currentSnapshot = this.grouping().map((col) => ({
      name: col.name,
      groupIndex: col.groupIndex,
      groupOrder: col.groupOrder,
    }));

    if (!isEqual(this.cachedGrouping, currentSnapshot)) {
      this.customTableService.reloadTable();
    }

    if (this.scrollRaf !== null) {
      cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = null;
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleColumn(_column: TColumn): void {
    const columnType = this.googleAnalyticsService.getColumnType(_column);
    const workspaceId = checkCustomWorkspaceId() || this.activeService.getActiveWorkspaceId();

    if (_column.hidden) {
      logEventInGTAG(EGoogleEventSite.SITE__COLUMNS__SHOW, {
        source: EGoogleEventCategory.SITE,
        type: columnType,
      });
    } else {
      logEventInGTAG(EGoogleEventSite.SITE__COLUMNS__HIDE, {
        source: EGoogleEventCategory.SITE,
        type: columnType,
      });
    }

    this.customTableService.getTable().toggleColumn(_column);

    this.preferencesService.savePreferences(workspaceId);
  }

  clearAll(): void {
    this.resetTableService.resetGrouping();
    this.hideDropdown();
  }

  dropGrouping(event: CdkDragDrop<TColumn[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const column = event.item.data as TColumn;
    this.groupButtonDropdownService.moveGroup(column.index, event.currentIndex);

    const workspaceId = checkCustomWorkspaceId() || this.activeService.getActiveWorkspaceId();
    this.preferencesService.savePreferences(workspaceId);
  }

  scrollHandler(event: Event): void {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }

    const target = event.target;

    if (this.scrollRaf !== null) {
      cancelAnimationFrame(this.scrollRaf);
    }

    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollPosition.set(target.scrollTop);
      this.scrolledToBottom.set(target.scrollHeight - target.scrollTop === target.clientHeight);
      this.scrollRaf = null;
    });
  }

  hideDropdown(): void {
    this.dropdownService.hideDropdown();
  }

  deleteGroup(column: TColumn): void {
    this.groupButtonDropdownService.removeGroup(column);

    logEventInGTAG(EGoogleEventGrouping.DELETE_GROUPING, {
      source: EGoogleEventCategory.GROUPING,
      type: this.googleAnalyticsService.getColumnType(column),
    });
  }

  addEmptyGroup(): void {
    this.emptyGroupsVisible.update((arr) => [...arr, 1]);
  }

  deleteEmptyGroup(): void {
    this.emptyGroupsVisible.update((arr) => arr.slice(0, -1));
  }

  updateEmptyGroupColumn(column: TColumn): void {
    this.groupButtonDropdownService.addGroup(column, ESortOrder.ASC);

    logEventInGTAG(EGoogleEventGrouping.ADD_GROUPING, {
      source: EGoogleEventCategory.GROUPING,
      type: this.googleAnalyticsService.getColumnType(column),
    });

    this.emptyGroupsVisible.update((arr) => arr.slice(0, -1));
  }

  saveGroupsToView(): void {
    const requestWorkspaceId = checkCustomWorkspaceId() || this.workspace?.workspaceId;
    this.activeView.columns = this.updateViewColumnsGrouping();
    this.activeView.collapsedGroups = Array.from(GET_COLLAPSED_GROUPS());

    this.saveViewProcessing.set(true);

    this.savedViewsService
      .saveToView(this.activeView, requestWorkspaceId)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to save grouping to view'),
        ),
        finalize(() => this.saveViewProcessing.set(false)),
      )
      .subscribe();
  }

  private updateViewColumnsGrouping(): TColumn[] {
    // We need to only update grouping and leave sorting, position, width and hidden as is
    const columns = this.tableColumnsService.getColumns();

    return this.activeView.columns.map((column) => {
      // If we enable saved views for overview we'll also need to check custom field ids because name won't be enough
      const matchingColumn = columns.find((c) => c.name === column.name);

      if (matchingColumn) {
        column.groupIndex = matchingColumn.groupIndex;
        column.groupOrder = matchingColumn.groupOrder;
      } else {
        column.groupIndex = null;
        column.groupOrder = null;
      }

      return column;
    });
  }

  updateOrder(item: ESortOrder, column: TColumn): void {
    this.groupButtonDropdownService.changeGroupOrder(item, column);
  }

  updateColumn(column: TColumn, oldColumn: TColumn): void {
    this.groupButtonDropdownService.replaceGroup(column, oldColumn);

    logEventInGTAG(EGoogleEventGrouping.UPDATE_GROUP, {
      source: EGoogleEventCategory.GROUPING,
      type: this.googleAnalyticsService.getColumnType(column),
    });
  }
}
